<template>
  <div
    id="neighborhood-detail"
    :class="['community-' + route.params.community, { 'ca-community': isCaCommunity }]"
  >
    <MoleculesLoadingOverlay v-if="loading" />
    <div v-else>
      <OrganismsUpcomingEventBanner
        v-if="upcomingEvent.uri"
        :organism="upcomingEvent"
      />
      <OrganismsBWBoxOverlay
        :organism="brandWindow"
        :class="hideTabbedContent ? 'two-col-buttons' : 'three-col-buttons'"
      />
      <OrganismsFlexibleContentColumns
        id="icon-columns"
        :organism="neighborhoodFeatures"
      />
      <!-- Disabled due to client request -->
      <!-- <OrganismsSimpleCentered :organism="simpleCentered" /> -->
      <div class="alternating-content-sections">
        <OrganismsSplitContent
          v-for="item in alternatingContent"
          :organism="item"
        />
      </div>
      <OrganismsTabbedContent
        v-if="!hideTabbedContent"
        id="see-homes"
        :key="tabbedContentKey"
        :organism="tabbedContent"
      >
        <template v-for="item in tabbedContent.item" #[item.id]>
          <h2
            v-if="item.id === 'floorplans'"
            class="text-2xl leading-7 sm:leading-10 sm:text-[32px] font-bold font-archer text-center text-gray-900 pt-12"
          >
            {{
              getNeighborhoodNavSubtitle(
                neighborhood.types,
                neighborhood.basePrice > 5000 ? getRoundedPrice(neighborhood.basePrice) : null,
                neighborhood.neighborhood?.status,
                true,
              )
            }}
          </h2>

          <div
            v-for="collection in neighborhoodCollections"
            class="pb-12 last:pb-28 mx-auto max-w-[1220px]"
          >
            <AtomsCollectionButton
              v-if="showCollectionHeader(collection, productsByCollection)"
              :collection="collection"
              :class="[
                'flex items-center justify-center mx-auto !my-0 !py-12 text-center [&>span]:font-bold [&>span]:text-[32px] [&>img]:w-11 w-fit',
              ]"
              disableShadow
              coloredText
            />
            <OrganismsProductGrid
              v-if="
                collection?.includes('easyhouse') &&
                productsByCollection?.easyHouse?.length
              "
              :organism="productsByCollection.easyHouse"
              :type="productsByCollection.type"
              :neighborhood="neighborhood"
              :preserveOrder="usingPickedFloorplans"
            />
            <OrganismsProductGrid
              v-if="
                collection?.includes('wee-cottage') &&
                productsByCollection?.weeCottage?.length
              "
              :organism="productsByCollection.weeCottage"
              :type="productsByCollection.type"
              :neighborhood="neighborhood"
              :preserveOrder="usingPickedFloorplans"
            />
            <OrganismsProductGrid
              v-if="
                collection?.includes('deed-restricted-wc') &&
                productsByCollection?.deedRestrictedWC?.length
              "
              :organism="productsByCollection.deedRestrictedWC"
              :type="productsByCollection.type"
              :neighborhood="neighborhood"
              :preserveOrder="usingPickedFloorplans"
            />
            <OrganismsProductGrid
              v-if="
                collection?.includes('limited-edition') &&
                productsByCollection?.limitedEdition?.length
              "
              :organism="productsByCollection.limitedEdition"
              :type="productsByCollection.type"
              :neighborhood="neighborhood"
              :preserveOrder="usingPickedFloorplans"
            />
            <OrganismsProductGrid
              v-if="
                collection?.includes('new-beginnings') &&
                productsByCollection?.newBeginnings?.length
              "
              :organism="productsByCollection.newBeginnings"
              :type="productsByCollection.type"
              :neighborhood="neighborhood"
              :preserveOrder="usingPickedFloorplans"
            />
          </div>
        </template>
      </OrganismsTabbedContent>
      <OrganismsGalleryGrid
        id="featured-photos-gallery"
        v-if="galleryGrid"
        class="mx-auto mb-24"
        :organism="galleryGrid"
      />
      <div class="mx-auto max-w-screen-xl px-8">
        <Organisms :organisms="organisms" />
      </div>
      <OrganismsSiteMapCTA
        id="site-map"
        v-if="siteMapCTA.link1"
        :organism="siteMapCTA"
      />
      <OrganismsFormCTA :organism="formCTA" />
      <OrganismsMapWithInfo id="map-with-info" :organism="mapWithInfo" />
      <OrganismsPopularNeighborhoods
        v-if="!isCaCommunity"
        :organism="popularNeighborhoods"
      />
      <OrganismsTextCTA
        id="cta-be-the-first"
        class="pt-[60px] pb-12 mx-auto max-w-screen-xl my-20"
        :organism="beTheFirst"
      />
      <OrganismsModalForm>
        <header class="mb-4">
          <h2 class="mb-4 text-4xl font-semibold text-black font-archer">
            Be the First
          </h2>
          <p v-if="!isFormSubmitted" class="text-lg text-gray-500">
            Please provide us your information and we will get back to you.
          </p>
        </header>
        <FormsHubSpotForm
          :form="{ id: isCaCommunity ? HUBSPOT_FORM_ID_CA : HUBSPOT_FORM_ID_CO }"
          @submit="isFormSubmitted = true"
        />
      </OrganismsModalForm>
    </div>
  </div>
</template>

<style>
/* CA communities: hide footer content under the mountain graphic, matching
   rebuilding-altadena-ca.vue */
body:has(#neighborhood-detail.ca-community) {
  .site-footer > div {
    display: none !important;
  }
}

#neighborhood-detail {
  &.community-lanterns-at-rock-creek {
  }

  .brand-window-box-overlay .buttons {
    @apply md:!grid md:grid-cols-3 mx-auto;

    a {
      @apply !text-[15px] leading-4 uppercase font-medium px-6 md:whitespace-nowrap;
    }
  }
  .brand-window-box-overlay.two-col-buttons .buttons {
    @apply md:grid-cols-2;

    .button-1-wrapper {
      @apply hidden;
    }
  }

  #icon-columns {
    @apply text-center text-white pt-10 pb-14 px-8;

    img {
      @apply w-14 inline-block pb-4;
    }

    .feature-title {
      @apply font-arial text-lg uppercase font-bold;
    }

    .description {
      @apply font-arial text-lg;
    }
  }

  .image img {
    @apply w-[1147px] max-w-full;
  }

  .tabbed-content {
    header {
      background-image: linear-gradient(101deg, #367ea0 0%, #4dadce 97%);
      .header-content {
        @apply max-w-[556px];
      }
    }
    .tabs-component-tabs {
      @apply w-[556px] mx-auto;
    }

    .tabs-component-panels {
      @apply mb-0;
    }
  }

  #community-dynamic-organisms {
    @apply sm:pt-0;
  }

  .wysiwyg-block {
    h1 {
      @apply sm:max-w-[80%] max-w-full m-auto text-3xl font-semibold md:text-5xl font-archer;
    }
  }

  .simple-centered {
    @apply sm:mb-20 mb-10;
  }

  .split-content {
    @apply sm:mb-20 mb-10 py-0;
  }

  .alternating-content-sections .split-content:last-child {
    @apply mb-0 pb-0 sm:mb-0 sm:pb-0;
  }

  .tabs-component-panels {
    @apply sm:mb-20 mb-10;
  }

  #community-dynamic-organisms {
    .brand-window-text-overlay {
      h1 {
        @apply mb-0 text-gray-800 drop-shadow-none sm:text-[3rem] sm:leading-[3.25rem];
      }
      h2 {
        @apply mb-4 text-bc-blue sm:text-[3rem] font-semibold font-archer drop-shadow-none sm:leading-[3.25rem];
      }
      p {
        @apply text-gray-500 text-xl;
      }
    }
  }
}

</style>

<script setup>
import { useAsyncQuery } from "#imports";
import { GET_NEIGHBORHOOD } from "~/graphql/GetNeighborhood";
import { GET_NEIGHBORHOOD_ORGANISMS } from "~/graphql/GetNeighborhoodOrganisms";
import states from "us-state-converter";
import { storeToRefs } from "pinia";
const route = useRoute();
const breadcrumbs = useBreadcrumbs();
const { breadcrumbsList } = storeToRefs(breadcrumbs);
// Pass an explicit, stable `key`. Without it, @nuxtjs/apollo derives the
// useAsyncData key by hashing the query + a Vue `reactive()` variables proxy,
// which hashes differently on the Node prerender vs the browser. The mismatched
// key means the client can't find the SSR/ISR payload, so it refetches on
// hydration - data briefly goes empty, `pending` flips true, and the loading
// overlay covers the page (the double-load). A fixed key makes server and
// client agree so the payload is reused and there is no client refetch.
const { data, pending } = await useAsyncQuery({
  query: GET_NEIGHBORHOOD,
  variables: { uri: route.path },
  key: `community-neighborhood:${route.params.location}/${route.params.community}`,
});
const neighborhoodDetails = useNeighborhoodDetails();
const { mapInfo } = storeToRefs(neighborhoodDetails);
const modalForm = useModalForm();
const isFormSubmitted = ref(false);
const tabbedContentKey = ref(0);

// useAsyncQuery (not useQuery) so the organisms render server-side and hydrate
// from the ISR payload instead of refetching on the client. Explicit key for
// the same server/client hash-stability reason as the neighborhood query above.
const { data: organismsData } = await useAsyncQuery({
  query: GET_NEIGHBORHOOD_ORGANISMS,
  variables: { uri: route.path },
  key: `community-organisms:${route.params.location}/${route.params.community}`,
});

const organisms = computed(() => {
  return organismsData.value?.organisms?.hlOrganisms?.hlOrganism ?? [];
});

// NOTE: This page is served by the `/homes/**` ISR route rule (nuxt.config.ts),
// so it is rendered + cached server-side. A previous `definePageMeta({ ssr: false })`
// here conflicted with that rule: the cached HTML showed content, then the client
// re-ran useAsyncQuery (no payload was serialized for an ssr:false page), flipping
// `pending` back to true and re-showing the loading overlay -> the double-load.
// Leaving SSR on lets the ISR payload hydrate, so there is a single render.

// Only show the full-screen overlay on the true initial load (no data yet).
// On prod the page is served from the ISR cache with payload extraction, so
// the Apollo cache isn't hydrated in time and useAsyncQuery refetches once on
// the client - flipping `pending` back to true. useAsyncData keeps `data`
// populated during that background refetch, so gating on `!data` lets the
// revalidation happen silently instead of re-triggering the loading overlay.
const loading = computed(() => pending.value && !data.value?.neighborhood);

const neighborhood = computed(() => {
  return data?.value?.neighborhood ?? null;
});

// California (Altadena rebuild) communities swap the Colorado copy, HubSpot
// forms, footer, and popular neighborhoods for the CA equivalents. Keyed on
// the URL's location segment (/homes/california/...) so it holds even before
// the neighborhood post exists, with the neighborhood's state field as a
// fallback (abbreviation or full name).
const isCaCommunity = computed(() => {
  if (route.params.location === "california") return true;
  const state = neighborhood.value?.neighborhood?.state?.trim() ?? "";
  return state.toUpperCase() === "CA" || state.toLowerCase() === "california";
});

// HubSpot "Be the First" modal form IDs. CA is the same form the Altadena
// plan pages use.
const HUBSPOT_FORM_ID_CO = "767c334f-0893-4757-8fa6-fd4b253bd691";
const HUBSPOT_FORM_ID_CA = "0b9fb411-b311-4a6d-9c48-b81996a5f6b0";

const neighborhoods = computed(() => {
  return data?.value?.neighborhoods?.nodes ?? [];
});

const plans = computed(() => {
  return data?.value?.plans ?? [];
});

const models = computed(() => {
  return data?.value?.models ?? [];
});

const homes = computed(() => {
  return data?.value?.homes ?? [];
});

const locations = computed(() => {
  return data?.value?.locations ?? [];
});

const computedBreadcrumbs = computed(() => {
  return [
    { title: "Our Neighborhoods", link: "/our-neighborhoods/" },
    {
      title: neighborhood.value?.title
        ? neighborhood.value.title +
          ", " +
          neighborhood.value?.neighborhood?.city
        : "",
    },
  ];
});

watch(computedBreadcrumbs, () => {
  breadcrumbsList.value = computedBreadcrumbs.value;
});

const upcomingEvent = computed(() => {
  if (!neighborhood.value) return { title: null, uri: null };
  return {
    title: neighborhood.value.neighborhood?.upcomingEvent?.title,
    uri: neighborhood.value.neighborhood?.upcomingEvent?.uri,
  };
});

const neighborhoodCollections = computed(() => {
  if (!neighborhood.value) return [];
  return getCollectionsByNeighborhood(
    neighborhood.value.slug,
    plans.value.nodes,
    homes.value.nodes,
    models.value.nodes,
    [neighborhood.value.collection?.collection],
  );
});

const neighborhoodFeatures = computed(() => {
  return {
    type: "flexible-content-columns",
    backgroundColor: "#387592",
    containerWidth: "full-width",
    item: neighborhood.value?.neighborhood?.neighborhoodFeatures ?? [],
  };
});

const simpleCentered = computed(() => {
  const city = neighborhood.value?.neighborhood?.city;
  const state = neighborhood.value?.neighborhood?.state;
  return {
    backgroundColor: "#EEEEEE",
    backgroundImage: {
      sourceUrl:
        neighborhood.value?.neighborhood?.basicContentImage?.sourceUrl ||
        "https://wp.livebouldercreek.com/wp-content/uploads/2022/05/BCN_Brand_2021_easyHouse_04_LG.jpg",
    },
    backgroundImageOpacity: 0.1,
    containerWidth: "full-width",
    title1: "New homes in",
    title2: city && state ? `${city}, ${states.fullName(state)}` : "",
    text1: neighborhood.value?.neighborhood?.description ?? "",
  };
});

const alternatingContent = computed(() => {
  return Array.isArray(neighborhood.value?.neighborhood?.alternatingContent)
    ? neighborhood.value.neighborhood.alternatingContent.map((item, index) => {
        return {
          backgroundColor: index % 2 ? "#f0f0f0" : "#ffffff",
          splitContentLayout: index % 2 ? "image-text" : "text-image",
          title1: item.title,
          description: item.description,
          button1: item.button,
          button1Text: item.buttonText,
          link1: item.link,
          videoEmbed: item.videoEmbed,
          image: item.image,
          alt: item.image?.alt,
          fullBleed: true,
        };
      })
    : [];
});

const beTheFirst = computed(() => {
  return {
    title1: "Be the First",
    description: `<p>Be the first to know about our ${isCaCommunity.value ? "California" : "Colorado"} neighborhoods and homes, including early access to pricing, new home plans and VIP events.</p>`,
    button1: true,
    button1Text: "Get Updates",
    link1: "#modal-form",
  };
});

// Tabs with nothing to show are dropped from `tabbedContent`, so once none are
// left there is no section to render.
const hideTabbedContent = computed(() => {
  return tabbedContent.value.item.length === 0;
});

const brandWindow = computed(() => {
  const neighborhoodData = neighborhood.value?.neighborhood;
  return {
    type: "brand-window",
    collectionIcons: neighborhoodCollections.value,
    title1: neighborhood.value?.title ?? "",
    title2: neighborhoodData?.city
      ? `${neighborhoodData.city}, ${neighborhoodData.state}`
      : "",
    title3: getNeighborhoodNavSubtitle(
      neighborhood.value?.types,
      neighborhood.value?.basePrice > 5000 ? getRoundedPrice(neighborhood.value?.basePrice) : null,
      neighborhoodData?.status,
      true,
    ),
    text1:
      getNeighborhoodStatusLabel(neighborhoodData?.status) ?? "",
    description: null,
    button1Color: "button-blue",
    button2Color: "button-white",
    button3Color: "button-yellow",
    button1: true,
    button1Text: "See Homes",
    link1: "#see-homes",
    button2: true,
    button2Text: "Get Directions",
    button2Target: "_blank",
    link2: neighborhoodData?.latitude
      ? `https://www.google.com/maps/place/${neighborhoodData.latitude},${neighborhoodData.longitude}`
      : "",
    button3: true,
    button3Text: "View Location",
    link3: "#map-with-info",
    containerWidth: "full-width",
    brandWindowLayout: "box-overlay",
    backgroundImage: neighborhoodData?.thumbnail,
    backgroundVideoFile: neighborhoodData?.backgroundVideoFile,
  };
});

const addTriggers = () => {
  const modalFormTriggers = document.querySelectorAll("a[href$='#modal-form']");

  modalFormTriggers.forEach((node) => {
    // Clone & replace node to remove existing event listeners
    const clonedNode = node.cloneNode(true);
    node.parentNode.replaceChild(clonedNode, node);

    clonedNode.addEventListener("click", (event) => {
      // Add event listener
      event.preventDefault();
      modalForm.openModal();
    });
  });
};

onUpdated(() => {
  addTriggers();
});

const popularNeighborhoods = computed(() => {
  if (!neighborhood.value) return { backgroundColor: "#f0f0f0", items: [], plans: [] };
  return {
    backgroundColor: "#f0f0f0",
    items: getPopularNeighborhoods(
      neighborhood.value.neighborhood?.popularNeighborhoods,
      neighborhoods.value,
      neighborhood.value.databaseId,
    ),
    plans: plans.value.nodes,
  };
});

// Maps each collection slug that can appear in `neighborhoodCollections` to the
// product bucket the panel renders for it. Kept in sync with the ProductGrid
// list in the template.
const COLLECTION_PRODUCT_KEYS = [
  ["easyhouse", "easyHouse"],
  ["wee-cottage", "weeCottage"],
  ["deed-restricted-wc", "deedRestrictedWC"],
  ["limited-edition", "limitedEdition"],
  ["new-beginnings", "newBeginnings"],
];

// A tab panel only renders grids for collections this neighborhood is tagged
// with, so a tab is only worth showing when one of those collections actually
// has products. Anything else would render an empty panel.
const hasVisibleProducts = (products) => {
  return neighborhoodCollections.value.some((collection) =>
    COLLECTION_PRODUCT_KEYS.some(
      ([slug, key]) => collection?.includes(slug) && products?.[key]?.length,
    ),
  );
};

const tabbedContent = computed(() => {
  const items = [];
  const neighborhoodData = neighborhood.value?.neighborhood;

  const showModelsTab = hasVisibleProducts(modelHomesByCollection.value);
  const showFloorplansTab = hasVisibleProducts(floorplansByCollection.value);
  const showQMIsTab = hasVisibleProducts(quickMoveInByCollection.value);

  if (showModelsTab) {
    items.push({ name: "Model Homes", id: "model-homes" });
  }
  if (showFloorplansTab) {
    items.push({ name: "Floorplans", id: "floorplans" });
  }
  if (showQMIsTab) {
    items.push({ name: "Quick Move-In", id: "quick-move-in" });
  }

  return {
    containerWidth: "default",
    collectionIcons: neighborhoodCollections.value,
    title1: neighborhood.value?.title ? `New homes at ${neighborhood.value.title}` : "",
    title2: neighborhoodData?.city
      ? `${neighborhoodData.city}, ${neighborhoodData.state}`
      : "",
    item: items,
    defaultTabHash: showFloorplansTab
      ? "floorplans"
      : showQMIsTab
        ? "quick-move-in"
        : items[0]?.id,
    loadingLabel: "Loading homes…",
  };
});

const modelHomesByCollection = computed(() => {
  const neighborhoodSlug = neighborhood.value?.slug;
  if (!neighborhoodSlug) return { type: "model-homes", easyHouse: [], weeCottage: [], deedRestrictedWC: [], limitedEdition: [], newBeginnings: [] };
  return {
    type: "model-homes",
    easyHouse: models?.value?.nodes?.filter((model) => {
      const collection =
        model.collection?.collection ??
        model.modelDetails?.floorplan?.floorplanDetails?.collection ??
        "";
      const modelNeighborhoodSlug = model.modelDetails?.neighborhood?.slug;
      return (
        collection.includes("easyhouse") &&
        modelNeighborhoodSlug === neighborhoodSlug
      );
    }),
    weeCottage: models?.value?.nodes?.filter((model) => {
      const collection =
        model.collection?.collection ??
        model.modelDetails?.floorplan?.floorplanDetails?.collection ??
        "";
      const modelNeighborhoodSlug = model.modelDetails?.neighborhood?.slug;
      return (
        collection.includes("wee-cottage") &&
        !collection.includes("deed-restricted-wc") &&
        modelNeighborhoodSlug === neighborhoodSlug
      );
    }),
    deedRestrictedWC: models?.value?.nodes?.filter((model) => {
      const collection =
        model.collection?.collection ??
        model.modelDetails?.floorplan?.floorplanDetails?.collection ??
        "";
      const modelNeighborhoodSlug = model.modelDetails?.neighborhood?.slug;
      return (
        collection.includes("deed-restricted-wc") &&
        modelNeighborhoodSlug === neighborhoodSlug
      );
    }),
    limitedEdition: models?.value?.nodes?.filter((model) => {
      const collection =
        model.collection?.collection ??
        model.modelDetails?.floorplan?.floorplanDetails?.collection ??
        "";
      const modelNeighborhoodSlug = model.modelDetails?.neighborhood?.slug;
      return (
        collection.includes("limited-edition") &&
        modelNeighborhoodSlug === neighborhoodSlug
      );
    }),
    newBeginnings: models?.value?.nodes?.filter((model) => {
      const collection =
        model.collection?.collection ??
        model.modelDetails?.floorplan?.floorplanDetails?.collection ??
        "";
      const modelNeighborhoodSlug = model.modelDetails?.neighborhood?.slug;
      return (
        collection.includes("new-beginnings") &&
        modelNeighborhoodSlug === neighborhoodSlug
      );
    }),
  };
});

// Track if we're using manually picked floorplans
const usingPickedFloorplans = computed(() => {
  const pickedFloorplans = neighborhood.value?.neighborhood?.pickFloorplans;
  return pickedFloorplans && Array.isArray(pickedFloorplans) && pickedFloorplans.length > 0;
});

const floorplansByCollection = computed(() => {
  // Check if manually picked floorplans exist
  const pickedFloorplans = neighborhood.value?.neighborhood?.pickFloorplans;
  
  // If manually picked floorplans are set, use them in the order they were picked
  if (pickedFloorplans && Array.isArray(pickedFloorplans) && pickedFloorplans.length > 0) {
    // Map the picked floorplan IDs to full plan data from plans.value.nodes
    // This preserves the order from pickFloorplans
    const pickedFloorplanIds = pickedFloorplans.map(fp => fp.databaseId);
    
    const orderedPlans = pickedFloorplanIds
      .map(id => plans?.value?.nodes?.find(plan => plan.databaseId === id))
      .filter(plan => plan && !isPlanHidden(plan)); // Remove undefined + plans flagged not available
    
    // Filter picked plans by collection (they may have different collections)
    if (orderedPlans.length > 0) {
      return {
        type: "floorplans",
        easyHouse: orderedPlans.filter(plan => {
          const collection = plan.floorplanDetails?.collection || '';
          return collection.includes("easyhouse");
        }),
        weeCottage: orderedPlans.filter(plan => {
          const collection = plan.floorplanDetails?.collection || '';
          return collection.includes("wee-cottage") && !collection.includes("deed-restricted-wc");
        }),
        deedRestrictedWC: orderedPlans.filter(plan => {
          const collection = plan.floorplanDetails?.collection || '';
          return collection.includes("deed-restricted-wc");
        }),
        limitedEdition: orderedPlans.filter(plan => {
          const collection = plan.floorplanDetails?.collection || '';
          return collection.includes("limited-edition");
        }),
        newBeginnings: orderedPlans.filter(plan => {
          const collection = plan.floorplanDetails?.collection || '';
          return collection.includes("new-beginnings");
        }),
      };
    }
    
    // Fallback if no plans found
    return {
      type: "floorplans",
      easyHouse: [],
      weeCottage: [],
      deedRestrictedWC: [],
      limitedEdition: [],
      newBeginnings: [],
    };
  }
  
  // Otherwise, fall back to automatic filtering of all plans
  const neighborhoodSlug = neighborhood.value?.slug;
  if (!neighborhoodSlug) {
    return {
      type: "floorplans",
      easyHouse: [],
      weeCottage: [],
      deedRestrictedWC: [],
      limitedEdition: [],
      newBeginnings: [],
    };
  }
  // Exclude plans flagged "Plan Not Available" (hidden from the front end,
  // but still published so their child specs stay in the third-party feeds).
  const visiblePlanNodes = (plans?.value?.nodes || []).filter(
    (plan) => !isPlanHidden(plan)
  );
  return {
    type: "floorplans",
    easyHouse: visiblePlanNodes.filter((plan) => {
      return (
        plan.floorplanDetails?.collection?.includes("easyhouse") &&
        plan.floorplanDetails?.neighborhood?.slug === neighborhoodSlug
      );
    }),
    weeCottage: visiblePlanNodes.filter((plan) => {
      const collection = plan.floorplanDetails?.collection || '';
      return collection.includes("wee-cottage") &&
        !collection.includes("deed-restricted-wc") &&
        plan.floorplanDetails?.neighborhood?.slug === neighborhoodSlug;
    }),
    deedRestrictedWC: visiblePlanNodes.filter((plan) => {
      const collection = plan.floorplanDetails?.collection || '';
      return collection.includes("deed-restricted-wc") &&
        plan.floorplanDetails?.neighborhood?.slug === neighborhoodSlug;
    }),
    limitedEdition: visiblePlanNodes.filter((plan) => {
      return (
        plan.floorplanDetails?.collection?.includes("limited-edition") &&
        plan.floorplanDetails?.neighborhood?.slug === neighborhoodSlug
      );
    }),
    newBeginnings: visiblePlanNodes.filter((plan) => {
      return (
        plan.floorplanDetails?.collection?.includes("new-beginnings") &&
        plan.floorplanDetails?.neighborhood?.slug === neighborhoodSlug
      );
    }),
  };
});

const quickMoveInByCollection = computed(() => {
  const neighborhoodSlug = neighborhood.value?.slug;
  if (!neighborhoodSlug) return { type: "quick-move-in", easyHouse: [], weeCottage: [], deedRestrictedWC: [], limitedEdition: [], newBeginnings: [] };
  
  const results = {
    type: "quick-move-in",
    easyHouse: homes?.value?.nodes?.filter((home) => {
      const collection =
        home.collection?.collection ??
        home.qmiHomeDetails?.floorplan?.floorplanDetails?.collection ??
        "";
      const homeNeighborhoodSlug = home.qmiHomeDetails?.neighborhood?.slug;
      return (
        collection.includes("easyhouse") &&
        homeNeighborhoodSlug === neighborhoodSlug
      );
    }),
    weeCottage: homes?.value?.nodes?.filter((home) => {
      const collection =
        home.collection?.collection ??
        home.qmiHomeDetails?.floorplan?.floorplanDetails?.collection ??
        "";
      const homeNeighborhoodSlug = home.qmiHomeDetails?.neighborhood?.slug;
      return (
        collection.includes("wee-cottage") &&
        !collection.includes("deed-restricted-wc") &&
        homeNeighborhoodSlug === neighborhoodSlug
      );
    }),
    deedRestrictedWC: homes?.value?.nodes?.filter((home) => {
      const collection =
        home.collection?.collection ??
        home.qmiHomeDetails?.floorplan?.floorplanDetails?.collection ??
        "";
      const homeNeighborhoodSlug = home.qmiHomeDetails?.neighborhood?.slug;
      return (
        collection.includes("deed-restricted-wc") &&
        homeNeighborhoodSlug === neighborhoodSlug
      );
    }),
    limitedEdition: homes?.value?.nodes?.filter((home) => {
      const collection =
        home.collection?.collection ??
        home.qmiHomeDetails?.floorplan?.floorplanDetails?.collection ??
        "";
      const homeNeighborhoodSlug = home.qmiHomeDetails?.neighborhood?.slug;
      return (
        collection.includes("limited-edition") &&
        homeNeighborhoodSlug === neighborhoodSlug
      );
    }),
    newBeginnings: homes?.value?.nodes?.filter((home) => {
      const collection =
        home.collection?.collection ??
        home.qmiHomeDetails?.floorplan?.floorplanDetails?.collection ??
        "";
      const homeNeighborhoodSlug = home.qmiHomeDetails?.neighborhood?.slug;
      return (
        collection.includes("new-beginnings") &&
        homeNeighborhoodSlug === neighborhoodSlug
      );
    }),
  };
  
  return results;
});

// `currentTab` is a shared store value: it is empty until the tabs component
// mounts and emits `changed`, and the plan/QMI detail pages write their own tab
// ids into it. Resolve to this page's default tab in both cases so the panel is
// never rendered against an undefined product set.
const activeTab = computed(() => {
  const tabIds = tabbedContent.value.item.map((item) => item.id);
  if (tabIds.includes(neighborhoodDetails.currentTab)) {
    return neighborhoodDetails.currentTab;
  }
  return tabIds.includes(tabbedContent.value.defaultTabHash)
    ? tabbedContent.value.defaultTabHash
    : tabIds[0];
});

const productsByCollection = computed(() => {
  switch (activeTab.value) {
    case "floorplans":
      return floorplansByCollection.value;
    case "model-homes":
      return modelHomesByCollection.value;
    case "quick-move-in":
      return quickMoveInByCollection.value;
  }
});

const showCollectionHeader = (iconId, products) => {
  switch (iconId) {
    case "easyhouse":
      return products?.easyHouse?.length;
    case "wee-cottage":
      return products?.weeCottage?.length;
    case "deed-restricted-wc":
      return products?.deedRestrictedWC?.length;
    case "limited-edition":
      return products?.limitedEdition?.length;
    case "new-beginnings":
      return products?.newBeginnings?.length;
  }
};

const siteMapCTA = computed(() => {
  const neighborhoodData = neighborhood.value?.neighborhood;
  return {
    containerWidth: "full-width",
    title1: neighborhood.value?.title && neighborhoodData?.city
      ? `Experience the ${neighborhood.value.title} homes for sale in ${neighborhoodData.city}, ${neighborhoodData.state ?? "CO"}`
      : "",
    backgroundColor: "rgba(137, 136, 48, 0.8)",
    backgroundImage: {
      sourceUrl:
        "https://wp.livebouldercreek.com/wp-content/uploads/2023/04/divider-map-bg.jpg",
    },
    image: {
      sourceUrl:
        "https://wp.livebouldercreek.com/wp-content/uploads/2016/11/siteMap-thumb-color-v3.jpg",
    },
    backgroundImageOpacity: "0.4",
    buttonColors: ["button-white"],
    button1: true,
    button1Text: "See homesites",
    button1Target: "_blank",
    link1: neighborhoodData?.siteMapPdfEPub ?? false,
  };
});

const formCTA = computed(() => {
  // Same copy + form as the form-cta organism on /rebuilding-altadena-ca/
  if (isCaCommunity.value) {
    return {
      containerWidth: "full-width",
      title1: "Let's start a conversation.",
      description: `<p>Our team is here to listen. Tell us about your situation, and a dedicated team member will reach out as soon as possible to let you know how we can help.</p>`,
      image: {
        sourceUrl: "/img/osc-seth-2.png",
      },
      hubspotFormId: "f76c924c-1c9f-4568-aade-129d4df816ce",
      hiddenFormFields: [],
    };
  }

  return {
    containerWidth: "full-width",
    title1: "Curious about this neighborhood?",
    description: `<p>Whether you're just getting started or ready to schedule a tour, our online team is here to help.</p><p>Call <a href="tel:303-309-0088">303-309-0088</a> or send us a message.</p>`,
    image: {
      sourceUrl: "/img/osc-seth-2.png",
    },
    hubspotFormId: "e474d5b6-a106-45e3-9e66-63179ae80735",
    hiddenFormFields: [
      {
        inputSelector: 'input[name="communities_of_interest__c"]',
        inputValue: neighborhood.value?.neighborhood?.hubspotValue ?? "",
      },
    ],
  };
});

watch(neighborhood, () => {
  const form =
    document.querySelector("#hsForm_e474d5b6-a106-45e3-9e66-63179ae80735") ??
    false;

  if (!form || !neighborhood.value) return;

  const hiddenFields = [
    {
      inputSelector: 'input[name="communities_of_interest__c"]',
      inputValue: neighborhood.value?.neighborhood?.hubspotValue ?? "",
    },
  ];

  if (Array.isArray(hiddenFields)) {
    hiddenFields.forEach((field) => {
      const input = form.querySelector(field.inputSelector);
      if (input) {
        input.value = field.inputValue;
      }
    });
  }
});

const galleryGrid = computed(() => {
  const galleryImages = neighborhood.value?.neighborhood?.photoGallery || [];

  if (!galleryImages.length) {
    return null;
  }

  return {
    type: "gallery-grid",
    gallery: galleryImages,
    thumbnailCount: 10,
    backgroundColor: "#111827",
  };
});

const mapWithInfo = computed(() => {
  const neighborhoodData = neighborhood.value?.neighborhood;
  const hours = computed(() => {
    const salesOfficeLocation = Array.isArray(locations?.value?.nodes)
      ? locations?.value?.nodes.find(
          (location) =>
            location?.locationDetails?.neighborhood?.[0]?.slug ===
            neighborhood.value?.slug,
        )
      : {};

    return (
      salesOfficeLocation?.locationDetails?.hours ??
      neighborhoodData?.hours
    );
  });

  return {
    markerTitle: neighborhood.value?.title ?? "",
    title1: "Stop by for a visit",
    address1: neighborhoodData?.address1 ?? "",
    address2: neighborhoodData?.address2 ?? "",
    city: neighborhoodData?.city ?? "",
    state: neighborhoodData?.state ?? "",
    zip: neighborhoodData?.zip ?? "",
    latitude: neighborhoodData?.latitude ?? null,
    longitude: neighborhoodData?.longitude ?? null,
    hours: hours.value,
    phone: neighborhoodData?.phone ?? "",
    salesTeamMembers: neighborhoodData?.saleTeamMembers ?? [],
    legal: neighborhoodData?.legal ?? "",
  };
});

const TAB_HASHES = ["#model-homes", "#floorplans", "#quick-move-in"];

// Anchors from the old site that are still linked externally.
const LEGACY_HASH_MAPPINGS = {
  "#tab-floorplans": "#floorplans",
  "#tab-qmi-homes": "#quick-move-in",
  "#tab-model-homes": "#model-homes",
};

// The tabs component ids its panels `<tab-id>-pane`, so a tab hash like
// `#floorplans` matches no element of its own - the tabbed section as a whole
// is what those links mean. Anything else is a plain in-page anchor.
const hashTarget = (currentHash) => {
  if (!currentHash) return null;
  if (TAB_HASHES.includes(currentHash)) {
    return document.querySelector("#see-homes");
  }
  try {
    return document.querySelector(currentHash);
  } catch {
    // Hashes that aren't valid selectors (`#modal-form?foo`, `#2`, ...).
    return null;
  }
};

const nextFrame = () => new Promise(requestAnimationFrame);

// Events that mean the visitor has taken the scroll over themselves.
const SCROLL_INTERRUPTS = ["wheel", "touchstart", "keydown"];

const onInterrupt = (handler) => {
  SCROLL_INTERRUPTS.forEach((event) =>
    window.addEventListener(event, handler, { once: true, passive: true }),
  );
  return () =>
    SCROLL_INTERRUPTS.forEach((event) =>
      window.removeEventListener(event, handler),
    );
};

// A smooth scroll reads badly on a page that is still settling: while the hero
// image/video above the tabs loads, the layout shifts under the animation.
// Wait for `load` (capped, because a slow third-party asset shouldn't hold the
// scroll hostage) and a couple of painted frames. Resolves false if the visitor
// started scrolling in the meantime - yanking the page then would be worse than
// not scrolling at all.
const waitForSettledLayout = async () => {
  let interrupted = false;
  const stopListening = onInterrupt(() => {
    interrupted = true;
  });

  try {
    if (document.readyState !== "complete") {
      await Promise.race([
        new Promise((resolve) =>
          window.addEventListener("load", resolve, { once: true }),
        ),
        new Promise((resolve) => setTimeout(resolve, 1000)),
      ]);
    }
    // rAF callbacks run before their frame paints, so two of them means the
    // settled layout is actually on screen before the animation starts.
    await nextFrame();
    await nextFrame();
  } finally {
    stopListening();
  }

  return !interrupted;
};

const SCROLL_DURATION_MS = 700;

const easeInOutQuad = (t) =>
  t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;

// Animate the scroll by hand rather than via `behavior: "smooth"`. Chrome
// silently degrades every smooth scroll - CSS `scroll-behavior` included - to an
// instant jump when the OS reports `prefers-reduced-motion: reduce` (Windows
// animation effects off), and cancels an in-flight one when the layout shifts,
// so asking the browser to animate isn't dependable here. Driving the frames
// ourselves also means this anchor scroll glides for every visitor, which is
// what was asked for.
let cancelActiveScroll = null;

const scrollToElement = (target) => {
  // Both entry points below can fire for the same load; a second animation
  // fighting the first one for `window.scrollTo` would visibly stutter.
  cancelActiveScroll?.();

  const from = window.scrollY;
  const startedAt = performance.now();
  let cancelled = false;
  const stopListening = onInterrupt(() => {
    cancelled = true;
  });

  cancelActiveScroll = () => {
    cancelled = true;
    stopListening();
  };

  const step = (now) => {
    if (cancelled) {
      stopListening();
      return;
    }

    // Recomputed every frame: anything above the section that is still loading
    // can move it while we're on the way there.
    const to = window.scrollY + target.getBoundingClientRect().top;
    const progress = Math.min((now - startedAt) / SCROLL_DURATION_MS, 1);

    // "instant" is required: `html` carries `scroll-smooth`, and the default
    // "auto" would hand each of these steps back to the browser to animate.
    window.scrollTo({
      top: from + (to - from) * easeInOutQuad(progress),
      behavior: "instant",
    });

    if (progress < 1) {
      requestAnimationFrame(step);
    } else {
      stopListening();
    }
  };

  requestAnimationFrame(step);
};

// Normalize a legacy hash, then scroll to whatever it points at. Returns the
// element it scrolled to, or null when there is nothing (yet) to scroll to.
const scrollToUrlHash = (currentHash) => {
  if (!currentHash) return null;

  const mapped = LEGACY_HASH_MAPPINGS[currentHash];
  if (mapped) {
    // Writing the hash back is also what makes the tabs component - which
    // selects on `hashchange` - land on the mapped tab.
    window.location.hash = mapped;
  }

  const target = hashTarget(mapped ?? currentHash);
  if (target) scrollToElement(target);
  return target;
};

onMounted(async () => {
  const urlHash = window.location.hash;
  if (urlHash) {
    // Nothing else scrolls for a hash that is already in the URL on the first
    // render: `app/router.options.ts` deliberately skips scrollBehavior on the
    // initial navigation, no element matches a tab hash for the browser to jump
    // to on its own, and the `loading` watcher below never fires because the ISR
    // payload hydrates with data already in hand. Wait for the tabs component
    // to render its (client-only) panel and for the page to settle, so the
    // scroll actually animates instead of snapping.
    await nextTick();
    if (await waitForSettledLayout()) {
      scrollToUrlHash(urlHash);
    }
  }

  window.addEventListener(
    "hashchange",
    () => {
      const urlHash = window.location.hash;
      if (urlHash === "#modal-form") {
        modalForm.openModal();
      }
      scrollToUrlHash(urlHash);
    },
    false,
  );

  addTriggers();
});

// Cold loads (client-side navigation into this page, or an empty ISR payload)
// have no tabbed section to scroll to at mount - scroll once the data lands.
watch(loading, async (newLoading, oldLoading) => {
  if (!oldLoading || newLoading) return;

  await nextTick();
  if (await waitForSettledLayout()) {
    scrollToUrlHash(window.location.hash);
  }
});
</script>
